/**
 * RXNT Integration SDK launch without <rxnt-launch-button>.
 * Matches sdk.min.js form POST so we can set form.target to a named popup window
 * (SDK hardcodes target="_blank" which opens a new tab).
 */
import {
  clearRxntBrowserSessionActive,
  markRxntBrowserSessionActive,
  openRxntEhrWindow,
  RXNT_EHR_WINDOW_NAME,
} from './rxntDeepLinkUtils';

export function getRxntLaunchPostPath(baseUrl) {
  const url = String(baseUrl || '');
  if (url.includes('localhost') || url.includes('127.0.0.1')) {
    return '/launch';
  }
  return '/ehr-gateway/launch';
}

function appendHiddenInput(form, name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
}

/** Same field names as RXNT sdk.min.js (patientfirstName, patientaddress1, …). */
function appendPatientDataFields(form, patientData) {
  if (!patientData || typeof patientData !== 'object') {
    return;
  }
  Object.entries(patientData).forEach(([key, value]) => {
    if (key === 'address' && value && typeof value === 'object') {
      Object.entries(value).forEach(([addrKey, addrVal]) => {
        if (addrVal != null) {
          appendHiddenInput(form, `patient${addrKey}`, String(addrVal));
        }
      });
      return;
    }
    if (value != null) {
      appendHiddenInput(form, `patient${key}`, String(value));
    }
  });
}

/**
 * POST launch to RXNT in the named popup window (not _blank tab).
 * @returns {boolean} false if popup blocked
 */
export function submitRxntLaunchForm(baseUrl, accessToken, launchData) {
  if (typeof document === 'undefined') {
    return false;
  }

  const popup = openRxntEhrWindow('about:blank');
  if (!popup) {
    return false;
  }

  const normalizedBase = String(baseUrl || '').replace(/\/$/, '');
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${normalizedBase}${getRxntLaunchPostPath(baseUrl)}`;
  form.target = RXNT_EHR_WINDOW_NAME;
  form.style.display = 'none';

  appendHiddenInput(form, 'accessToken', accessToken);
  appendHiddenInput(form, 'targetDoctorId', String(launchData?.targetDoctorId ?? ''));
  appendHiddenInput(form, 'externalOrderId', String(launchData?.externalOrderId ?? ''));
  appendHiddenInput(
    form,
    'activatePatientPortal',
    launchData?.activatePatientPortal ? 'true' : 'false'
  );
  appendHiddenInput(form, 'returnUrl', String(launchData?.returnUrl ?? ''));
  appendHiddenInput(form, 'hm', launchData?.hm ? 'true' : 'false');
  appendPatientDataFields(form, launchData?.patientData);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);

  return true;
}

/**
 * Full launch: token + launchData factories → POST in popup window.
 */
export async function performRxntSdkLaunch({
  baseUrl,
  getAccessToken,
  getLaunchData,
  onLaunchSuccess,
  onError,
}) {
  try {
    const accessToken = await getAccessToken();
    const launchData = await getLaunchData();
    if (!accessToken) {
      throw new Error('Failed to obtain RXNT access token');
    }
    if (!launchData || typeof launchData !== 'object') {
      throw new Error('Failed to build RXNT launch context');
    }
    const opened = submitRxntLaunchForm(baseUrl, accessToken, launchData);
    if (!opened) {
      throw new Error('Allow pop-ups for Snap to open the RXNT window.');
    }
    markRxntBrowserSessionActive();
    if (onLaunchSuccess) {
      onLaunchSuccess();
    }
    return true;
  } catch (err) {
    clearRxntBrowserSessionActive();
    const message = err?.message || 'RXNT launch failed';
    if (onError) {
      onError(message);
    } else {
      console.error('RXNT launch error', err);
    }
    return false;
  }
}
